import { useEffect, useState } from 'react';
import { Country, FeatureOption, Filters, Option, PageName } from '@utils/pageConfig/Types';
import {
  handleStackedChartSegmentSelect,
  HandleStackedChartSegmentSelectParams,
  isSelectDiffPgFromAppliedFunction,
} from '@components/chartpickers/SegmentByChartPickersUtil';
import {
  getDisabledFilterKeysByAppliedCurrency,
  removeFeaturesFromDropdownBasedOnSelectedFeatures,
  removeFeaturesFromDropdownBasedOnSideFilters,
} from '@utils/exploreUtils/ExploreUtils';
import { ExploreChartFiltersType } from '@utils/exploreUtils/ExploreType';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { ProductType } from '@pages/product-analyzer/Product';
import { getFeatureSelector } from '@utils/hook/selector/ShowPerformanceWithinSelector';
import { useSelector } from 'react-redux';
import { IRootState } from '@state/reducers/Reducers';
import { InitialFilter } from '@state/reducers/initial-filter/InitialFiltersReducer';
import { initialFeature } from '@utils/FilterUtils';

export interface UseGetStackedChartSegmentFiltersProps {
  allFeatureValues: Filters[];
  allFilters: Filters[];
  productType: ProductType;
  countriesToQuery: Country[];
  showPerformanceWithin: FeatureOption;
  setShowPerformanceWithin: (feature: FeatureOption) => void;
  appliedProductGroups: Option[] | undefined;
  selectedProductGroups: Option[] | undefined;
  isRestoreFromSavedView: boolean;
  segmentByFeature: FeatureOption;
  setSegmentByFeature: (feature: FeatureOption) => void;
}

export const useGetStackedChartSegmentFilters = ({
  allFeatureValues,
  allFilters,
  productType,
  countriesToQuery,
  showPerformanceWithin,
  setShowPerformanceWithin,
  appliedProductGroups,
  selectedProductGroups,
  isRestoreFromSavedView,
  segmentByFeature,
  setSegmentByFeature,
}: UseGetStackedChartSegmentFiltersProps) => {
  const pagesConfig = usePageConfig();
  const [appliedFeatureValues, setAppliedFeatureValues] = useState<Filters[]>(allFeatureValues);
  const initialFiltersState = useSelector((state: IRootState) => state.initialFilters);
  const filterKeysToDisable = getDisabledFilterKeysByAppliedCurrency(allFilters, pagesConfig, productType);
  const isChannelFilterAllowed =
    initialFiltersState.initialFilters.find((filter: InitialFilter) => filter.country === countriesToQuery[0]?.id)
      ?.isChannelFilterAllowed || false; //NOSONAR typescript:S6606
  const filteredShowPerformanceWithinFeatureList = removeFeaturesFromDropdownBasedOnSelectedFeatures(
    ExploreChartFiltersType.ShowPerformanceWithinFeature,
    appliedFeatureValues,
    segmentByFeature,
    appliedProductGroups,
    isChannelFilterAllowed,
  );

  const filteredSegmentByFeatureList = removeFeaturesFromDropdownBasedOnSelectedFeatures(
    ExploreChartFiltersType.SegmentByFeature,
    appliedFeatureValues,
    showPerformanceWithin,
    appliedProductGroups,
    isChannelFilterAllowed,
  );

  //React Hooks
  useEffect(() => {
    if (isSelectDiffPgFromAppliedFunction(selectedProductGroups, appliedProductGroups)) {
      return;
    }

    const filteredAllFeatureValuesBasedOnSideFilters = removeFeaturesFromDropdownBasedOnSideFilters(
      allFeatureValues,
      pagesConfig,
      countriesToQuery,
      appliedProductGroups,
    );

    setAppliedFeatureValues(filteredAllFeatureValuesBasedOnSideFilters);
  }, [allFeatureValues]);

  useEffect(() => {
    if (showPerformanceWithin == initialFeature && !isRestoreFromSavedView) {
      const updatedShowPerformanceWithin = getFeatureSelector(
        filteredShowPerformanceWithinFeatureList,
        showPerformanceWithin,
        filterKeysToDisable,
        PageName.EXPLORE,
      );
      setShowPerformanceWithin(updatedShowPerformanceWithin);
    }
  }, [appliedProductGroups, countriesToQuery, appliedFeatureValues]);

  useEffect(() => {
    const handleStackedChartSegmentSelectParams: HandleStackedChartSegmentSelectParams = {
      allFeatureValues: filteredSegmentByFeatureList,
      selectedPgOptions: selectedProductGroups,
      appliedPgOptions: appliedProductGroups,
      selectedSegmentOption: segmentByFeature,
      setSelectedSegmentOption: setSegmentByFeature,
      showPerformanceWithinOption: showPerformanceWithin,
      hasUserClickedApplyAtSideFilters: false,
      disabledFeatureValues: filterKeysToDisable,
    };

    handleStackedChartSegmentSelect(handleStackedChartSegmentSelectParams);
  }, [showPerformanceWithin, appliedFeatureValues]);

  return {
    filteredShowPerformanceWithinFeatureList,
    filteredSegmentByFeatureList,
    segmentByFeature,
    setSegmentByFeature,
  };
};
